import { onAuthStateChanged, onIdTokenChanged } from 'firebase/auth'
import * as Sentry from '@sentry/react'
import { firebaseAuth } from 'helpers/firebase'

let initialized = false
let authReady = false
let cachedToken = null

/** @type {Set<() => void>} */
const authReadyListeners = new Set()
/** @type {Set<(token: string | null) => void>} */
const tokenListeners = new Set()

const notifyAuthReady = () => {
  for (const fn of authReadyListeners) fn()
}

const notifyToken = (token) => {
  for (const fn of tokenListeners) fn(token)
}

export const initTokenManager = () => {
  if (initialized) return
  initialized = true

  onAuthStateChanged(firebaseAuth, () => {
    if (!authReady) {
      authReady = true
      notifyAuthReady()
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'authReady',
        level: 'info',
      })
    }
  })

  onIdTokenChanged(firebaseAuth, async (user) => {
    try {
      cachedToken = user ? await user.getIdToken() : null
    } catch (_) {
      cachedToken = null
    }
    notifyToken(cachedToken)
  })
}

export const isAuthReady = () => authReady

// React subscription helpers (for useSyncExternalStore)
export const subscribeAuthReady = (listener) => {
  authReadyListeners.add(listener)
  return () => authReadyListeners.delete(listener)
}

export const getAuthReadySnapshot = () => authReady

export const subscribeToken = (listener) => {
  tokenListeners.add(listener)
  return () => tokenListeners.delete(listener)
}

export const getTokenSnapshot = () => cachedToken

export const waitForAuthReady = async (timeoutMs = 5000) => {
  initTokenManager()
  if (authReady) return true
  return await new Promise((resolve) => {
    let settled = false
    const timer = setTimeout(() => {
      if (settled) return
      settled = true
      authReadyListeners.delete(onReady)
      resolve(authReady)
    }, timeoutMs)

    const onReady = () => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      authReadyListeners.delete(onReady)
      resolve(true)
    }

    authReadyListeners.add(onReady)
  })
}

export const getCachedToken = () => cachedToken

export const waitForToken = async (timeoutMs = 5000) => {
  initTokenManager()
  if (cachedToken) return cachedToken

  return await new Promise((resolve) => {
    let settled = false
    const timer = setTimeout(() => {
      if (settled) return
      settled = true
      tokenListeners.delete(onToken)
      resolve(cachedToken)
    }, timeoutMs)

    const onToken = (token) => {
      if (settled) return
      if (!token) return
      settled = true
      clearTimeout(timer)
      tokenListeners.delete(onToken)
      resolve(token)
    }

    tokenListeners.add(onToken)
  })
}

